// 2P AI — Sessione dal vivo in stile chat (§8.8), 1:1 con 2pai-chat.html. Cablata a useSessionSocket.
// L'area di approvazione (diff + Consenti/Blocca + Face ID) è 1:1 ma il gate reale (decide/long-poll)
// atterra in R4: in R2 gli eventi approval_request non vengono ancora emessi.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, ApiError } from '../lib/api';
import { withStepUp } from '../lib/webauthn';
import { useSessionSocket } from '../lib/ws';
import { useProjects, useCancelSession } from '../lib/queries';
import { Icon } from '../components/Icon';
import { Markdown } from '../components/Markdown';
import { Spark } from '../components/Spark';
import type { PersistedEvent, SessionSummary } from '../lib/types';
import { useQuery } from '@tanstack/react-query';

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function toolInputView(toolName: string, input: unknown): string {
  const i = rec(input);
  if (toolName === 'Bash' && typeof i.command === 'string') return i.command;
  if (typeof i.file_path === 'string') {
    if (typeof i.new_string === 'string') return `${i.file_path}\n\n- ${i.old_string ?? ''}\n+ ${i.new_string}`;
    if (typeof i.content === 'string') return `${i.file_path}\n\n${i.content}`;
    return String(i.file_path);
  }
  if (typeof i.pattern === 'string') return `pattern: ${i.pattern}`;
  return JSON.stringify(i, null, 2);
}

// Argomento sintetico in stile CLI: Bash→comando, file→nome file, Grep→pattern, WebFetch→url.
function cliArg(toolName: string, input: unknown): string {
  const i = rec(input);
  if (toolName === 'Bash' && typeof i.command === 'string') return i.command.length > 64 ? i.command.slice(0, 64) + '…' : i.command;
  if (typeof i.file_path === 'string') return i.file_path.split('/').pop() || i.file_path;
  if (typeof i.pattern === 'string') return String(i.pattern);
  if (typeof i.url === 'string') return String(i.url);
  if (typeof i.path === 'string') return String(i.path);
  return '';
}
// Numero di righe "vere" di un output (ignora le newline finali).
function lineCount(s: string): number {
  const t = s.replace(/\n+$/, '');
  return t === '' ? 0 : t.split('\n').length;
}
function clip(s: string, n: number): string {
  const one = s.trim().replace(/\s+/g, ' ');
  return one.length > n ? one.slice(0, n) + '…' : one;
}

// Esito a UNA riga come il terminale di Claude Code (⎿ "Lette 440 righe", "12 righe", …).
// Il contenuto pieno NON finisce in chat: si vede solo espandendo la riga. È questo che rende
// la chat leggibile come CC nel terminale invece di un muro di output.
function toolResultSummary(toolName: string, out: string, isErr: boolean): string {
  if (isErr) {
    const first = out.split('\n').find((l) => l.trim() !== '') ?? 'Errore';
    return clip(first, 88);
  }
  const n = lineCount(out);
  if (toolName === 'Read') return `Lette ${n} righe`;
  if (toolName === 'Write') return 'Scritto';
  if (toolName === 'Grep') return `${n} ${n === 1 ? 'corrispondenza' : 'corrispondenze'}`;
  if (toolName === 'Glob' || toolName === 'LS') return `${n} ${n === 1 ? 'elemento' : 'elementi'}`;
  if (toolName === 'TodoWrite') return 'Elenco aggiornato';
  if (n === 0) return '(nessun output)';
  if (n === 1) return clip(out, 88);
  return `${n} righe`;
}
function diffSummary(d: { old: string; neu: string }): string {
  const add = d.neu === '' ? 0 : d.neu.split('\n').length;
  const del = d.old === '' ? 0 : d.old.split('\n').length;
  return `Aggiornato · +${add} −${del}`;
}

// Icona per categoria di tool (Material Symbols), come i chip attività della app Claude.
const TOOL_ICON: Record<string, string> = {
  Bash: 'terminal', Read: 'description', Write: 'edit_note', Edit: 'edit_note',
  NotebookEdit: 'edit_note', Grep: 'search', Glob: 'search', LS: 'folder_open',
  WebFetch: 'language', WebSearch: 'travel_explore', TodoWrite: 'checklist',
  TaskCreate: 'checklist', Task: 'account_tree', Agent: 'account_tree', Workflow: 'account_tree',
};

// Riga tool stile app Claude: chip minimale "icona + Nome · arg" (shimmer mentre gira),
// esito a UNA riga sotto; tap → espande (contenuto pieno / diff colorato).
function ToolLine({ ev, result }: { ev: PersistedEvent; result?: PersistedEvent }) {
  const [open, setOpen] = useState(false);
  const p = ev.payload;
  const toolName = String(p.tool_name ?? '');
  const arg = cliArg(toolName, p.input);
  const r = result?.payload;
  const isErr = r ? r.is_error === true : false;
  const out = r ? String(r.content ?? '') : '';
  const inp = rec(p.input);
  const diff = typeof inp.new_string === 'string' ? { old: String(inp.old_string ?? ''), neu: String(inp.new_string) } : null;
  const pending = !result && !diff; // tool ancora in corso (nessun risultato e nessun diff da mostrare)
  const summary = diff ? diffSummary(diff) : toolResultSummary(toolName, out, isErr);
  const canExpand = diff !== null || out !== '';
  const toggle = () => { if (canExpand) setOpen((v) => !v); };
  return (
    <div className={`tool${isErr ? ' err' : ''}${open ? ' open' : ''}${pending ? ' running' : ''}`}>
      <div className="trow" role={canExpand ? 'button' : undefined} tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? open : undefined} onClick={toggle}
        onKeyDown={canExpand ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : undefined}>
        <Icon name={TOOL_ICON[toolName] ?? 'build'} />
        <span className="tlbl">{toolName}{arg ? ` · ${arg}` : ''}</span>
      </div>
      {open && diff ? (
        <div className="tbody">
          <div className="tdiff">
            {diff.old.split('\n').map((l, idx) => <div key={`d${idx}`} className="ln del">- {l}</div>)}
            {diff.neu.split('\n').map((l, idx) => <div key={`a${idx}`} className="ln add">+ {l}</div>)}
          </div>
        </div>
      ) : open && out ? (
        <div className="tbody"><pre className={isErr ? 'err' : ''}>{out.slice(0, 20000)}</pre></div>
      ) : !pending ? (
        <div className={`tsum${isErr ? ' err' : ''}`} onClick={toggle}>{summary}</div>
      ) : null}
    </div>
  );
}

const DECIDED_LABEL: Record<string, string> = {
  allow: 'Consentita',
  deny: 'Bloccata',
  expired: 'Superata da una nuova richiesta',
};

function ApprovalCard({ ev, decision }: { ev: PersistedEvent; decision?: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const p = ev.payload;
  const toolName = String(p.tool_name ?? '');
  const input = rec(p.input);
  const destructive = p.destructive === true;
  const decided = decision !== undefined;
  const target = typeof input.file_path === 'string' ? input.file_path : (typeof input.command === 'string' ? input.command : toolName);
  const diff = typeof input.new_string === 'string'
    ? { old: String(input.old_string ?? ''), neu: String(input.new_string) }
    : null;

  async function decide(dec: 'allow' | 'deny') {
    setErr(null);
    const approvalId = String(p.approval_id ?? '');
    if (!approvalId) { setErr('ID approvazione mancante.'); return; }
    setBusy(true);
    try {
      // Gate: POST /api/approvals/{id}/decide. Le distruttive esigono X-StepUp (403
      // step_up_required): withStepUp esegue la cerimonia Face ID/passkey e ritenta.
      await withStepUp('approve_destructive', (t) => api.post(`/approvals/${approvalId}/decide`, { decision: dec }, t));
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 404 ? 'Approvazione non trovata (forse rimossa con la sessione).' : (e instanceof Error ? e.message : 'Errore'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`approval-${String(p.approval_id ?? ev.seq)}`} className={`approval${decided ? ' decided' : ''}`}>
      <div className="ah">
        <Icon name="front_hand" />
        <div>
          <div className="at">Vuole {diff ? 'modificare un file' : 'eseguire un’azione'}</div>
          <div className="as">{target}{destructive ? ' · azione distruttiva' : ''}</div>
        </div>
      </div>
      <div className="ab">
        <div className="tgt"><Icon name={diff ? 'edit' : 'terminal'} />{target}</div>
        <div className="codebar">
          <span className="cf">{diff ? 'modifica proposta' : 'comando'}</span>
        </div>
        {diff ? (
          <div className="diff">
            {diff.old.split('\n').map((l, i) => <div key={`d${i}`} className="ln del">- {l}</div>)}
            {diff.neu.split('\n').map((l, i) => <div key={`a${i}`} className="ln add">+ {l}</div>)}
          </div>
        ) : (
          <pre>{toolInputView(toolName, p.input)}</pre>
        )}
        {err ? <div className="note danger" style={{ marginTop: 10 }}><Icon name="error" /><span>{err}</span></div> : null}
      </div>
      <div className="af">
        {decided ? (
          <>
            <span className="bio">
              <Icon name={decision === 'allow' ? 'check_circle' : 'block'} />
              {DECIDED_LABEL[decision ?? ''] ?? decision}
            </span>
            <button type="button" className="btn-deny" disabled><Icon name="close" />Blocca</button>
            <button type="button" className="btn-allow" disabled><Icon name="check" />Consenti</button>
          </>
        ) : (
          <>
            {destructive ? <span className="bio"><Icon name="face" />Richiede Face ID</span> : null}
            <button type="button" className="btn-deny" disabled={busy} onClick={() => void decide('deny')}><Icon name="close" />Blocca</button>
            <button type="button" className="btn-allow" disabled={busy} onClick={() => void decide('allow')}><Icon name="check" />Consenti</button>
          </>
        )}
      </div>
    </div>
  );
}

// Aiuto comandi /slash (gestiti lato client, mai inviati al modello).
const SLASH_HELP = [
  'Comandi:',
  '/help — questa lista',
  '/clear — nuova sessione (stesso progetto)',
  '/model — scegli il modello (apre il compositore)',
  '/skip · /auto · /gate · /read — autonomia (nuova sessione)',
  '/usage — utilizzo token',
  '/exit · /quit — chiudi la sessione',
].join('\n');

// Chip autonomia nel composer (l'equivalente del chip "Manuale" della app Claude).
const MODE_META: Record<string, { label: string; icon: string }> = {
  gate: { label: 'Con conferma', icon: 'front_hand' },
  auto: { label: 'Autonomo', icon: 'bolt' },
  skip: { label: 'Skip totale', icon: 'fast_forward' },
  read: { label: 'Solo lettura', icon: 'visibility' },
};

const STATUS_LABEL: Record<string, { cls: string; label: string }> = {
  running: { cls: 'run', label: 'in corso' },
  awaiting_approval: { cls: 'wait', label: 'attende il tuo OK' },
  done: { cls: '', label: 'completata' },
  error: { cls: '', label: 'errore' },
  timeout: { cls: '', label: 'errore' },
  canceled: { cls: '', label: 'chiusa' },
  paused: { cls: '', label: 'a riposo' },
  queued: { cls: '', label: 'in coda' },
};

/**
 * Vero quando si scrive con una tastiera a schermo (telefono/tablet). Serve a decidere cosa fa
 * l'Invio nel compositore: su iOS non esiste Shift+Invio, quindi lì l'Invio deve andare a capo.
 * Si valuta una volta sola: chi apre l'app dal telefono non ci attacca una tastiera a metà chat,
 * e rivalutarlo a ogni tasto premuto non varrebbe il costo.
 */
const touchInput = typeof window !== 'undefined'
  && window.matchMedia?.('(pointer: coarse)').matches === true;

export function Console({ id }: { id: string }) {
  const [, navigate] = useLocation();
  const { events, streamText, formingTools, status, connected, interrupt } = useSessionSocket(id);
  const [text, setText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  // Eco ottimistico dell'invio: il testo compare SUBITO in chat (bolla "in invio") senza aspettare
  // il round-trip WS dell'evento user_text. Fixa il "scrivo e non succede nulla" — soprattutto con
  // modelli lenti (opus/xhigh) o quando il motore è occupato: prima il campo si svuotava e basta.
  const [pendingSend, setPendingSend] = useState<string | null>(null);
  const [cmdNote, setCmdNote] = useState<string | null>(null); // output locale dei comandi /slash
  const chatRef = useRef<HTMLElement>(null);
  const atBottomRef = useRef(true); // l'utente era vicino al fondo? (misurato SULLO scroll, non dopo il render)
  const didInitScroll = useRef(false); // primo salto in fondo all'apertura della sessione
  const taRef = useRef<HTMLTextAreaElement>(null);
  const projects = useProjects().data ?? [];
  const cancel = useCancelSession();

  const { data: session } = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.get<{ session: SessionSummary }>(`/sessions/${id}`).then((d) => d.session),
    retry: false,
    refetchInterval: 8000,
  });

  // Mappa tool_use_id → evento tool_result, per mostrare l'output sotto la riga `⏺` del tool.
  const resultByTool = useMemo(() => {
    const m: Record<string, PersistedEvent> = {};
    for (const e of events) if (e.type === 'tool_result') m[String(e.payload.tool_use_id)] = e;
    return m;
  }, [events]);

  // Esito per approval_id (allow/deny/expired): dà uno stato alle card (bottoni disattivati
  // sulle già decise/superate) e individua l'eventuale approvazione ANCORA in attesa.
  const decisionByApproval = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of events) {
      if (e.type === 'approval_decision') m[String(e.payload.approval_id)] = String(e.payload.decision ?? '');
    }
    return m;
  }, [events]);
  const pendingApproval = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e.type !== 'approval_request') continue;
      const id = String(e.payload.approval_id ?? '');
      if (decisionByApproval[id] === undefined) return { id, seq: e.seq };
      return null; // la più recente è già decisa: niente attesa
    }
    return null;
  }, [events, decisionByApproval]);

  // Autoscroll del CONTENITORE della chat (non del documento), solo se si è già vicini al fondo:
  // niente scrollIntoView "smooth" che combatteva con lo scroll dell'utente e con la tastiera iOS.
  useLayoutEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    // Primo caricamento della sessione: salta SEMPRE in fondo (apri → vedi subito l'ultimo messaggio).
    if (!didInitScroll.current && events.length > 0) {
      didInitScroll.current = true;
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
      return;
    }
    // Aggiornamenti successivi: segui il fondo solo se l'utente ci era già (altrimenti sta leggendo indietro).
    // useLayoutEffect: scrollHeight è già quello finale di questo render → niente ritardo/salti.
    if (!atBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [events, streamText, pendingSend, cmdNote]);

  // iOS non restringe 100dvh quando si apre la tastiera: pilotiamo l'altezza della shell dal
  // visual viewport (var --vvh), così la riga comando resta visibile senza far "saltare" la pagina.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const set = () => document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    set();
    vv.addEventListener('resize', set);
    return () => {
      vv.removeEventListener('resize', set);
      document.documentElement.style.removeProperty('--vvh');
    };
  }, []);

  // Eco ottimistico: appena l'evento reale user_text arriva via WS, togliamo la bolla "in invio"
  // così non resta duplicata (l'eco e l'evento hanno lo stesso testo → swap invisibile).
  useEffect(() => {
    if (!pendingSend) return;
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].type === 'user_text') {
        if (String(events[i].payload.text ?? '') === pendingSend) setPendingSend(null);
        break;
      }
    }
  }, [events, pendingSend]);

  // Lo stato via WS è autorevole solo a socket connessa; da disconnessi vince il polling HTTP
  // (uno stato WS rimasto in memoria da una socket morta non deve oscurare quello fresco).
  const liveStatus = (connected && status) || session?.status || status || 'queued';
  const st = STATUS_LABEL[liveStatus] ?? { cls: '', label: liveStatus };
  const projectName = session ? projects.find((p) => p.id === session.project_id)?.name ?? '' : '';
  const working = liveStatus === 'running' || liveStatus === 'awaiting_approval' || liveStatus === 'queued';

  // Riga di avanzamento: cosa sta facendo ORA (il tool in formazione, o l'ultimo tool_use del turno
  // in corso), senza riversare i blocchi dei tool in chat. Torna null a turno finito / da idle.
  const activity = useMemo(() => {
    const f = Object.values(formingTools) as Array<{ tool_name?: string }>;
    if (f.length) return `${f[0]?.tool_name || 'strumento'}…`;
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e.type === 'tool_use') return String(e.payload.summary ?? e.payload.tool_name ?? 'lavoro');
      if (e.type === 'assistant_text' || e.type === 'result') break;
    }
    return null;
  }, [events, formingTools]);

  // Modello REALMENTE in esecuzione: la stringa che l'SDK di Claude Code ha confermato nel suo
  // evento system/init (autorevole), NON quella solo richiesta al lancio. Su una ripresa Claude
  // può fare un downgrade silenzioso di un modello ad accesso speciale (es. fable-5 → opus-4-8
  // quando fable non è disponibile in quel momento): qui lo si vede e, se differisce, si avvisa.
  const runningModel = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e.type === 'system' && typeof e.payload.model === 'string' && e.payload.model) return e.payload.model;
    }
    return null;
  }, [events]);
  const requestedModel = session?.model ?? null;
  const shownModel = runningModel ?? requestedModel;
  const modelDrift = runningModel !== null && requestedModel !== null && runningModel !== requestedModel;

  function back() {
    navigate(session?.project_id ? `/p/${session.project_id}` : '/');
  }
  function stop() {
    if (working && !window.confirm('Questa sessione è viva. Vuoi interromperla?')) return;
    cancel.mutate(id);
  }
  // Comandi /slash in stile CLI: gestiti QUI, mai inviati al modello. Modello e autonomia di una
  // sessione viva non si cambiano (l'SDK li fissa al lancio) → quei comandi aprono il compositore.
  function runSlash(raw: string): void {
    const c = (raw.slice(1).trim().split(/\s+/)[0] || '').toLowerCase();
    const pid = session?.project_id;
    switch (c) {
      case 'help': setCmdNote(SLASH_HELP); break;
      case 'clear': navigate(pid ? `/p/${pid}/new` : '/new'); break;
      case 'usage': case 'cost': navigate('/usage'); break;
      case 'model':
        setCmdNote(runningModel
          ? `Modello in esecuzione (confermato dall'SDK di Claude): ${runningModel}${modelDrift ? ` — attenzione: avevi chiesto ${requestedModel}, ma non era disponibile e Claude ha fatto un downgrade automatico.` : ''}. Il modello si fissa a inizio sessione; per cambiarlo apri il compositore.`
          : `Il modello si sceglie a inizio sessione. Apro il compositore.`);
        if (pid && !runningModel) navigate(`/p/${pid}/new`);
        break;
      case 'skip': case 'auto': case 'gate': case 'read':
        setCmdNote(`"/${c}" si sceglie a inizio sessione (l'autonomia di una sessione viva non cambia). Apro il compositore.`);
        if (pid) navigate(`/p/${pid}/new`);
        break;
      case 'exit': case 'quit': cancel.mutate(id); back(); break;
      case 'resume': setCmdNote('La sessione riprende da sola: scrivi un messaggio.'); break;
      default: setCmdNote(`Comando non riconosciuto: /${c}. Usa /help per la lista.`);
    }
  }
  function onSend() {
    const t = text.trim();
    if (!t) return;
    // come in un terminale: 'exit'/'quit' chiudono la sessione invece di inviare il testo a Claude
    if (/^(exit|quit|\/exit|\/quit|:q|:wq)$/i.test(t)) { setText(''); cancel.mutate(id); back(); return; }
    // comandi /slash: gestiti localmente, mai inviati al modello
    if (t.startsWith('/')) { setText(''); if (taRef.current) taRef.current.style.height = 'auto'; runSlash(t); return; }
    setText('');
    setSendError(null);
    setCmdNote(null); // un messaggio vero pulisce l'output dell'ultimo comando
    setPendingSend(t); // eco immediato in chat: non aspettare il round-trip WS
    if (taRef.current) taRef.current.style.height = 'auto';
    // turno via HTTP: il control-plane lo instrada al motore della macchina giusta (multi-server).
    // L'eco (user_text) e la risposta tornano come eventi via WS. Se l'invio fallisce, il testo
    // torna nel campo e l'errore compare accanto alla riga comando (prima spariva in silenzio).
    void api.post(`/sessions/${id}/turns`, { text: t }).catch((e) => {
      setPendingSend(null); // invio fallito → togli l'eco ottimistico
      setText((cur) => cur || t);
      setSendError(e instanceof ApiError ? `${e.status} · ${e.message}` : 'Invio non riuscito');
    });
  }
  function grow() {
    const ta = taRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`; }
  }

  return (
    <div className="chatscreen">
      <header className="chead">
        <button className="back" type="button" title="Indietro" aria-label="Indietro" onClick={back}><Icon name="arrow_back" /></button>
        <div className="ti">
          <div className="t1">{session?.title ?? 'Sessione'}</div>
          <div className="t2">
            {projectName ? `${projectName} · ` : ''}<span className={st.cls}>{st.label}</span>{shownModel ? <> · <span className={modelDrift ? 'mdrift' : undefined}>{shownModel}{modelDrift ? ' ⚠' : ''}</span></> : null}
            {!connected ? <> · <span className="off">offline</span></> : null}
          </div>
        </div>
        <button className="hbtn stop" type="button" title="Chiudi sessione" aria-label="Chiudi sessione" onClick={stop}><Icon name="stop_circle" /></button>
      </header>

      {modelDrift ? (
        <div className="note warn modeldrift">
          <Icon name="warning" />
          <span>Hai chiesto <strong>{requestedModel}</strong>, ma sta girando <strong>{runningModel}</strong>: quel modello non era disponibile al momento e Claude ha fatto un downgrade automatico.</span>
        </div>
      ) : null}

      <main className="chat" ref={chatRef} onScroll={(e) => { const el = e.currentTarget; atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120; }}>
        {events.map((e) => {
          if (e.type === 'system') return <div key={e.seq} className="sys">Sessione avviata · <span className="mono">{String(e.payload.model ?? '')}</span></div>;
          if (e.type === 'user_text') return <div key={e.seq} className="u-msg">{String(e.payload.text ?? '')}</div>;
          if (e.type === 'assistant_text') return <div key={e.seq} className="a-msg"><Markdown text={String(e.payload.text ?? '')} /></div>;
          if (e.type === 'tool_use') return <ToolLine key={e.seq} ev={e} result={resultByTool[String(e.payload.tool_use_id)]} />;
          if (e.type === 'tool_result') return null;
          if (e.type === 'stderr') return <div key={e.seq} className="tool err"><div className="tbody"><pre className="err">{String(e.payload.text ?? '')}</pre></div></div>;
          if (e.type === 'approval_request') {
            return <ApprovalCard key={e.seq} ev={e} decision={decisionByApproval[String(e.payload.approval_id ?? '')]} />;
          }
          if (e.type === 'approval_decision') {
            const d = String(e.payload.decision ?? '');
            if (d === 'expired') return <div key={e.seq} className="sys">Richiesta superata da una nuova proposta</div>;
            const allowed = d === 'allow';
            const carried = e.payload.carried_over === true;
            return <div key={e.seq} className={allowed ? 'resolved' : 'resolved denied'}><Icon name={allowed ? 'check_circle' : 'block'} />{allowed ? (carried ? 'OK già dato: azione eseguita alla ripresa' : 'Hai consentito') : 'Hai bloccato'}</div>;
          }
          if (e.type === 'result') {
            const p = e.payload;
            const tk = rec(p.tokens);
            const tok = Number(tk.input ?? 0) + Number(tk.output ?? 0);
            return <div key={e.seq} className={p.is_error ? 'turn-result err' : 'turn-result'}>{p.is_error ? 'Errore' : 'Fatto'} · {String(p.num_turns ?? '')} tool{tok ? ` · ${Math.round(tok / 1000)}k token` : ''} · {Math.round(Number(p.duration_ms ?? 0) / 1000)}s</div>;
          }
          if (e.type === 'info') return <div key={e.seq} className="sys">{String(e.payload.message ?? e.payload.text ?? '')}</div>;
          if (e.type === 'workflow') {
            const w = e.payload;
            const agents = Number(w.agent_count ?? 0);
            return <div key={e.seq} className="sys"><span className="mono">{String(w.name ?? 'workflow')}</span> · {String(w.status ?? '')}{agents > 0 ? ` · ${agents} agenti` : ''}</div>;
          }
          return null;
        })}

        {cmdNote ? <div className="cmd-note">{cmdNote}</div> : null}
        {pendingSend ? <div className="u-msg pending">{pendingSend}</div> : null}
        {streamText ? <div className="a-msg"><Markdown text={streamText} /></div> : null}
        {(working || pendingSend) && !streamText ? (
          <div className="progress"><Spark /><span className="ptext">{activity ?? 'sta lavorando…'}</span></div>
        ) : null}
      </main>

      <div className="barwrap">
        {pendingApproval && ['running', 'awaiting_approval', 'paused', 'queued'].includes(liveStatus) ? (
          <div
            className="note info"
            role="button"
            tabIndex={0}
            style={{ marginBottom: 8, cursor: 'pointer' }}
            onClick={() => document.getElementById(`approval-${pendingApproval.id}`)?.scrollIntoView({ block: 'center' })}
          >
            <Icon name="front_hand" />
            <span>Claude attende il tuo OK: usa <strong>Consenti</strong> o <strong>Blocca</strong> sulla card. Tocca qui per vederla.</span>
          </div>
        ) : null}
        {sendError ? (
          <div className="note danger" style={{ marginBottom: 8 }}><Icon name="error" /><span>{sendError}</span></div>
        ) : null}
        <div className="barin">
          <textarea
            ref={taRef}
            className="tx"
            rows={1}
            placeholder={liveStatus === 'paused' ? 'Scrivi per riprendere la sessione…' : 'Scrivi un messaggio…'}
            value={text}
            onChange={(e) => { setText(e.target.value); grow(); }}
            enterKeyHint={touchInput ? 'enter' : 'send'}
            // Dal telefono l'Invio va A CAPO, non spedisce: con la tastiera di iOS non esiste
            // Shift+Invio, quindi un brief di tre righe usciva spezzato in tre messaggi — e ogni
            // pezzo faceva ripartire Claude su una richiesta monca. Si spedisce col pulsante, che
            // è lì accanto. Da tastiera vera resta com'era: Invio spedisce, Shift+Invio va a capo.
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey || touchInput) return;
              e.preventDefault();
              onSend();
            }}
          />
          <div className="brow">
            <button
              className="mode" type="button" title="Autonomia della sessione"
              aria-label={`Autonomia della sessione: ${(MODE_META[session?.permission_mode ?? ''] ?? MODE_META.gate).label}`}
              onClick={() => setCmdNote("L'autonomia non cambia a sessione viva: si sceglie a inizio sessione dal compositore (/gate /auto /skip /read).")}
            >
              <Icon name={(MODE_META[session?.permission_mode ?? ''] ?? MODE_META.gate).icon} />
              {(MODE_META[session?.permission_mode ?? ''] ?? MODE_META.gate).label}
            </button>
            <span className="spacer" />
            {working && !text.trim() ? (
              <button className="stop" type="button" title="Interrompi il turno" aria-label="Interrompi il turno" onClick={() => interrupt()}><i /></button>
            ) : (
              <button className="send" type="button" title="Invia" aria-label="Invia" disabled={!text.trim()} onClick={onSend}><Icon name="arrow_upward" /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
